import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Settings } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <div className="bg-primary-600 text-white py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link
                        to="/"
                        className="inline-flex items-center text-blue-100 hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Home
                    </Link>
                    <div className="flex items-center gap-3">
                        <Shield className="h-8 w-8" />
                        <h1 className="text-3xl font-bold">Privacy Policy</h1>
                    </div>
                    <p className="mt-2 text-blue-100">
                        Last updated:{' '}
                        {new Date().toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })}
                    </p>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="prose prose-lg max-w-none">
                    {/* Introduction */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Eye className="h-6 w-6 text-primary-600" />
                            Introduction
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                            OpenCorporates Ltd ("we," "our," or "us") operates the OpenLEIs website
                            and LEI search platform (the "Service"). This Privacy Policy explains
                            how we collect, use, disclose, and safeguard your information when you
                            use our Service.
                        </p>
                        <p className="text-gray-700 leading-relaxed mt-4">
                            By using our Service, you agree to the collection and use of information
                            in accordance with this policy. We are committed to protecting your
                            privacy and handling your data transparently and securely.
                        </p>
                    </section>

                    {/* Information We Collect */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Database className="h-6 w-6 text-primary-600" />
                            Information We Collect
                        </h2>

                        <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">
                            Information You Provide
                        </h3>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            <li>
                                Search queries and filters you enter when using our LEI search
                                functionality
                            </li>
                            <li>Feedback or communications you send to us</li>
                        </ul>

                        <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">
                            Information Automatically Collected
                        </h3>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            <li>
                                <strong>Usage Data:</strong> Pages visited, search terms, time spent
                                on pages, click patterns
                            </li>
                            <li>
                                <strong>Device Information:</strong> Browser type, operating system,
                                screen resolution
                            </li>
                            <li>
                                <strong>Log Data:</strong> IP address, access times, referring URLs
                            </li>
                        </ul>

                        <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">
                            Cookies and Tracking Technologies
                        </h3>
                        <p className="text-gray-700 leading-relaxed">
                            We use cookies and similar technologies to enhance your experience:
                        </p>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
                            <li>
                                <strong>Necessary Cookies:</strong> Essential for website
                                functionality and cannot be disabled
                            </li>
                            <li>
                                <strong>Analytics Cookies:</strong> Google Analytics to understand
                                website usage (requires consent)
                            </li>
                            <li>
                                <strong>Marketing Cookies:</strong> For personalized content and
                                advertisements (requires consent)
                            </li>
                        </ul>
                    </section>

                    {/* How We Use Information */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                            How We Use Your Information
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            We use the collected information for the following purposes:
                        </p>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            <li>Provide and maintain our LEI search Service</li>
                            <li>Improve and optimize website performance and user experience</li>
                            <li>Analyze usage patterns and trends to enhance our services</li>
                            <li>Respond to your inquiries and provide customer support</li>
                            <li>
                                Detect, prevent, and address technical issues or security threats
                            </li>
                            <li>Comply with legal obligations and protect our rights</li>
                        </ul>
                    </section>

                    {/* Data Sharing */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                            Information Sharing and Disclosure
                        </h2>

                        <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">
                            Third-Party Services
                        </h3>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            <li>
                                <strong>Google Analytics:</strong> Website analytics and user
                                behavior analysis (only with consent)
                            </li>
                            <li>
                                <strong>Cloud Infrastructure:</strong> AWS and similar services for
                                hosting and data processing
                            </li>
                        </ul>

                        <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">
                            We Do Not Sell Personal Information
                        </h3>
                        <p className="text-gray-700 leading-relaxed">
                            We do not sell, trade, or rent your personal information to third
                            parties. We may share information only in the following circumstances:
                        </p>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
                            <li>With your explicit consent</li>
                            <li>To comply with legal obligations or valid legal process</li>
                            <li>To protect our rights, property, or safety, or that of others</li>
                            <li>
                                In connection with a business transaction (merger, acquisition,
                                etc.)
                            </li>
                        </ul>
                    </section>

                    {/* Data Retention */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                            Data Retention
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                            We retain your personal information only as long as necessary to fulfill
                            the purposes outlined in this Privacy Policy, unless a longer retention
                            period is required by law. Analytics data is typically retained for 26
                            months, while log data is retained for security purposes for up to 12
                            months.
                        </p>
                    </section>

                    {/* Your Rights */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Settings className="h-6 w-6 text-primary-600" />
                            Your Privacy Rights
                        </h2>

                        <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">
                            Cookie Preferences
                        </h3>
                        <p className="text-gray-700 leading-relaxed mb-2">
                            You can manage your cookie preferences at any time by:
                        </p>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            <li>Clicking "Cookie Settings" in the website footer</li>
                            <li>Adjusting your browser settings to block or delete cookies</li>
                        </ul>

                        <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">
                            GDPR Rights (EU Residents)
                        </h3>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            <li>
                                <strong>Right to Access:</strong> Request copies of your personal
                                data
                            </li>
                            <li>
                                <strong>Right to Rectification:</strong> Request correction of
                                inaccurate data
                            </li>
                            <li>
                                <strong>Right to Erasure:</strong> Request deletion of your personal
                                data
                            </li>
                            <li>
                                <strong>Right to Restrict Processing:</strong> Request limitation of
                                data processing
                            </li>
                            <li>
                                <strong>Right to Data Portability:</strong> Request transfer of your
                                data
                            </li>
                            <li>
                                <strong>Right to Object:</strong> Object to data processing for
                                direct marketing
                            </li>
                        </ul>

                        <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">
                            US State Privacy Rights
                        </h3>
                        <p className="text-gray-700 leading-relaxed mb-2">
                            If you are a resident of California, Virginia, Colorado, or other states
                            with privacy laws, you may have additional rights:
                        </p>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            <li>
                                <strong>Right to Know:</strong> Information about data collection
                                and use
                            </li>
                            <li>
                                <strong>Right to Delete:</strong> Request deletion of personal
                                information
                            </li>
                            <li>
                                <strong>Right to Opt-Out:</strong> Opt out of data sharing (we don't
                                sell data)
                            </li>
                            <li>
                                <strong>Right to Non-Discrimination:</strong> Equal service
                                regardless of privacy choices
                            </li>
                        </ul>
                    </section>

                    {/* Security */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
                        <p className="text-gray-700 leading-relaxed">
                            We implement appropriate technical and organizational measures to
                            protect your personal information against unauthorized access,
                            alteration, disclosure, or destruction. This includes:
                        </p>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
                            <li>SSL/TLS encryption for data transmission</li>
                            <li>Secure hosting infrastructure with access controls</li>
                            <li>Regular security assessments and updates</li>
                            <li>Limited access to personal data on a need-to-know basis</li>
                        </ul>
                    </section>

                    {/* International Transfers */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                            International Data Transfers
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                            Our Service is hosted on servers that may be located outside your
                            country of residence. We ensure appropriate safeguards are in place for
                            international data transfers, including standard contractual clauses and
                            adequacy decisions where applicable.
                        </p>
                    </section>

                    {/* Children's Privacy */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                            Children's Privacy
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                            Our Service is not directed to children under the age of 13. We do not
                            knowingly collect personal information from children under 13. If you
                            are a parent or guardian and believe your child has provided personal
                            information, please contact us so we can delete such information.
                        </p>
                    </section>

                    {/* Changes to Policy */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                            Changes to This Privacy Policy
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                            We may update this Privacy Policy from time to time. We will notify you
                            of any changes by posting the new Privacy Policy on this page and
                            updating the "Last updated" date. Changes become effective immediately
                            upon posting.
                        </p>
                    </section>

                    {/* Contact Information */}
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            If you have questions about this Privacy Policy or want to exercise your
                            privacy rights, please contact us:
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-gray-700">
                                <strong>Email:</strong> data.protection@opencorporates.com
                            </p>
                            <p className="text-gray-700">
                                <strong>Address:</strong> OpenCorporates Ltd, Aston House, Cornwall
                                Avenue, London, N3 1LF, United Kingdom
                            </p>
                            <p className="text-gray-700">
                                <strong>Response Time:</strong> We will respond to your inquiry
                                within 30 days
                            </p>
                            <p></p>
                            <p className="text-gray-700">
                                If you wish to contact us for any other reason, we can be{' '}
                                <u>
                                    contacted{' '}
                                    <a href="https://opencorporates.atlassian.net/servicedesk/customer/portal">
                                        via our help desk.
                                    </a>
                                </u>
                            </p>
                        </div>
                    </section>

                    {/* Data Sources Notice */}
                    <section className="mb-8 bg-blue-50 p-6 rounded-lg border border-blue-200">
                        <h2 className="text-2xl font-semibold text-blue-900 mb-4">
                            About LEI Data
                        </h2>
                        <p className="text-blue-800 leading-relaxed">
                            The Legal Entity Identifier (LEI) data displayed on this platform is
                            sourced from public records provided by the Global Legal Entity
                            Identifier Foundation (GLEIF) and Local Operating Units (LOUs). This
                            data is publicly available and is not considered personal information
                            under privacy laws. Our platform simply aggregates and presents this
                            public data in a searchable format.
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;
